import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { DataState } from "../components/ui/DataState";
import { reportTypeLabel, type Report } from "../lib/types";

type Decision = "approve" | "reject" | "more_info_needed";

const DECISION_LABELS: Record<Decision, string> = {
  approve: "Approve",
  reject: "Reject",
  more_info_needed: "Request more info",
};

export default function VerifierQueue() {
  const api = useApi();
  const { user } = useCurrentUser();
  const [reports, setReports] = useState<Report[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    return api
      .get<{ reports: Report[] }>("/api/verifier/reports")
      .then((r) => {
        setReports(r.reports);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const isAdmin = user?.role === "platform_admin";
  const emptyMessage = isAdmin
    ? "No reports in the verification queue. Things look clear."
    : "No reports are assigned to you yet. A platform admin assigns reports to verifiers.";

  return (
    <div className="container-page py-12">
      <header>
        <h1 className="text-3xl font-semibold text-white">Verifier queue</h1>
        <p className="mt-2 text-slate-300">
          {isAdmin
            ? "All reports needing verification, newest first. As an admin you can also see unassigned items here."
            : "Reports assigned to you for verification, newest first. Decide whether each report is approved, rejected, or needs more info. Your decision and note are recorded in the audit log."}
        </p>
      </header>

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={reports}
          empty={!!reports && reports.length === 0}
          loadingMessage="Loading your verifier queue…"
          emptyMessage={emptyMessage}
        >
          {(items) => (
            <div className="grid gap-5">
              {items.map((r) => (
                <VerifierItem key={r.id} report={r} onReviewed={load} />
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}

function VerifierItem({
  report,
  onReviewed,
}: {
  report: Report;
  onReviewed: () => void;
}) {
  const api = useApi();
  const [decision, setDecision] = useState<Decision | "">("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!decision) {
      setError("Pick a decision before submitting.");
      return;
    }
    if (!note.trim()) {
      setError("A reviewer note is required for every decision.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.patch(`/api/verifier/reports/${report.id}/review`, {
        decision,
        reviewer_note: note,
      });
      setSubmitted(true);
      setNote("");
      setDecision("");
      onReviewed();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={urgencyTone(report.urgency)}>{report.urgency}</Badge>
        <Badge>{reportTypeLabel(report.report_type)}</Badge>
        {report.neighborhood && <Badge>{report.neighborhood}</Badge>}
        {report.organization_name && (
          <Badge tone="success">Org: {report.organization_name}</Badge>
        )}
        <Badge tone="warning">{report.status.replace(/_/g, " ")}</Badge>
      </div>
      <CardTitle className="mt-3 text-lg">
        {report.title ?? report.location ?? "Untitled report"}
      </CardTitle>
      {report.title && report.location && (
        <p className="mt-1 text-xs text-slate-400">{report.location}</p>
      )}
      <p className="mt-3 whitespace-pre-line text-sm text-slate-300">
        {report.description}
      </p>
      <div className="mt-2 text-xs text-slate-400">
        Submitted {report.created_at}
      </div>

      <form onSubmit={submit} className="surface-soft mt-5 grid gap-4 p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Select
            label="Decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value as Decision | "")}
          >
            <option value="">— Choose a decision —</option>
            {(Object.keys(DECISION_LABELS) as Decision[]).map((d) => (
              <option key={d} value={d}>
                {DECISION_LABELS[d]}
              </option>
            ))}
          </Select>
          {submitted && (
            <span className="text-xs text-emerald-300">Decision recorded.</span>
          )}
        </div>
        <Textarea
          required
          label="Reviewer note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          hint="What did you confirm or what's missing? This note is shown to the report's submitter."
        />
        {error && (
          <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit decision"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
